import React, { useMemo } from "react";
import {
  makeStyles,
  Paper,
  Box,
  Typography,
  Tooltip,
} from "@material-ui/core";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    margin: `${theme.spacing(2)}px 0`,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
  },
  empty: {
    color: theme.palette.grey[600],
    fontSize: "0.9rem",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: `${theme.spacing(0.75)}px 0`,
  },
  rank: {
    width: 22,
    color: theme.palette.grey[600],
    fontSize: "0.85rem",
    fontWeight: 600,
  },
  label: {
    flex: "0 0 28%",
    fontSize: "0.9rem",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  barWrap: {
    flex: 1,
    background: theme.palette.grey[200],
    height: 10,
    borderRadius: 5,
    overflow: "hidden",
  },
  bar: {
    height: "100%",
    background: "linear-gradient(90deg, #1976d2, #42a5f5)",
    borderRadius: 5,
  },
  count: {
    width: 70,
    textAlign: "right",
    fontSize: "0.85rem",
    color: theme.palette.grey[700],
  },
  meta: {
    color: theme.palette.grey[600],
    fontSize: "0.8rem",
    marginTop: theme.spacing(1),
  },
}));

const extractSkills = (jobs) => {
  const counts = new Map();
  let analyzed = 0;
  (jobs || []).forEach((job) => {
    const attrs = job.attributes || {};
    const buckets = [];
    const tryFields = [
      "field_skills",
      "field_required_skills",
      "field_technical_skills",
      "field_skills_required",
      "field_qualifications",
      "field_experience_requirements",
      "field_description",
    ];
    tryFields.forEach((f) => {
      const v = attrs[f];
      if (!v) return;
      if (Array.isArray(v)) {
        v.forEach((x) => buckets.push(typeof x === "string" ? x : (x && x.value) || ""));
      } else if (typeof v === "string") {
        buckets.push(v);
      } else if (v.value) {
        buckets.push(v.value);
      }
    });

    if (buckets.length === 0) return;
    analyzed += 1;
    const text = buckets
      .join(" ")
      .replace(/<[^>]*>/g, " ")
      .toLowerCase();

    const KNOWN = [
      "javascript", "typescript", "python", "java", "c#", "c++", "go", "rust",
      "ruby", "php", "swift", "kotlin", "scala", "react", "angular", "vue",
      "next.js", "node.js", "express", "django", "flask", "spring", "rails",
      "laravel", "graphql", "rest", "html", "css", "sass", "tailwind",
      "redux", "mongodb", "postgresql", "mysql", "redis", "elasticsearch",
      "aws", "azure", "gcp", "docker", "kubernetes", "terraform", "ansible",
      "ci/cd", "jenkins", "git", "linux", "agile", "scrum", "figma",
      "tensorflow", "pytorch", "machine learning", "data science",
      "product management", "ux", "ui", "design", "seo", "marketing",
    ];
    KNOWN.forEach((skill) => {
      const re = new RegExp(`(?:^|[^a-z0-9])${skill.replace(/[.+]/g, (c) => "\\" + c)}(?:$|[^a-z0-9])`, "i");
      if (re.test(text)) {
        counts.set(skill, (counts.get(skill) || 0) + 1);
      }
    });
  });

  const sorted = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);
  return { sorted, analyzed };
};

const SkillsInsights = ({ jobs }) => {
  const classes = useStyles();
  const { sorted, analyzed } = useMemo(() => extractSkills(jobs), [jobs]);

  if (!jobs || jobs.length === 0) {
    return null;
  }

  const max = sorted.length > 0 ? sorted[0][1] : 1;

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <TrendingUpIcon color="primary" />
        <Typography variant="h6" className={classes.title}>
          Top in-demand skills in your jobs
        </Typography>
      </Box>

      {sorted.length === 0 ? (
        <Typography className={classes.empty}>
          We couldn't detect specific skills in your job descriptions yet. Add
          common technology or skill keywords to your postings to see trends
          here.
        </Typography>
      ) : (
        <Box>
          {sorted.map(([skill, count], idx) => (
            <Box key={skill} className={classes.row}>
              <span className={classes.rank}>#{idx + 1}</span>
              <span className={classes.label}>{skill}</span>
              <Tooltip title={`Mentioned in ${count} job${count > 1 ? "s" : ""}`}>
                <div className={classes.barWrap}>
                  <div
                    className={classes.bar}
                    style={{ width: `${Math.max(8, (count / max) * 100)}%` }}
                  />
                </div>
              </Tooltip>
              <span className={classes.count}>
                {count} job{count > 1 ? "s" : ""}
              </span>
            </Box>
          ))}
          <Typography className={classes.meta}>
            Analyzed {analyzed} of {jobs.length} job posting{jobs.length > 1 ? "s" : ""}.
            Use this to spot skill demand and recalibrate your sourcing.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default SkillsInsights;
